/**
 * Portal host map for the three-portal split (AD #149).
 *
 * One Next.js app serves three authenticated portals as host-scoped
 * route namespaces:
 *
 *   - VirtualSpace (customer)  — my.virtualspace.xyz  → /portal/vs/*
 *   - Spatialist  (partner)    — pro.spatialist.xyz   → /portal/go/*
 *   - SpaceStation (internal)  — ops.virtualspace.xyz → /dashboard/*
 *     (`app.spatialist.xyz` aliases 308 here — Domain rule 4)
 *
 * The partner portal is served on EXACTLY ONE canonical host — portal
 * session cookies are host-only by design, so serving one portal on
 * two hosts would split sessions. The founder-adopted alias hosts
 * (`pro.virtualspace.xyz`, `pro.spatialist.com`) and the retired
 * `go.spatialist.xyz` 308-redirect to the canonical host instead (see
 * `PORTAL_HOST_ALIASES` + the host-conditional redirects in
 * `next.config.js`).
 *
 * `spatialist.com` stays the email/SMS deliverability domain and
 * routes by audience prefix (`/vs`, `/go`, `/hq`) — see
 * `resolveLinkRouterTarget`. Pure constant module (no env reads) so
 * client components can import it safely, mirroring
 * `virtual-space-host.ts`.
 */

export type PortalId = "virtualspace" | "spatialist" | "spacestation";

export const PORTAL_HOSTS: Record<PortalId, string> = {
  virtualspace: "my.virtualspace.xyz",
  spatialist: "pro.spatialist.xyz",
  spacestation: "ops.virtualspace.xyz",
};

/**
 * Non-canonical hosts that belong to a portal but never serve it —
 * they 308 to the canonical host (path + query preserved). Keyed by
 * bare lowercase host.
 */
export const PORTAL_HOST_ALIASES: Record<string, PortalId> = {
  "pro.virtualspace.xyz": "spatialist",
  "pro.spatialist.com": "spatialist",
  // Retired canonical host (AD #149 originally launched on `go.`).
  "go.spatialist.xyz": "spatialist",
  // Retired SpaceStation host (Domain rule 4 / AD #185). Keep bookmarks,
  // Twilio transition URLs, and in-flight email CTAs working.
  "app.spatialist.xyz": "spacestation",
  "www.app.spatialist.xyz": "spacestation",
  "app.spatialist.com": "spacestation",
  "www.app.spatialist.com": "spacestation",
};

export const PORTAL_ORIGINS: Record<PortalId, string> = {
  virtualspace: `https://${PORTAL_HOSTS.virtualspace}`,
  spatialist: `https://${PORTAL_HOSTS.spatialist}`,
  spacestation: `https://${PORTAL_HOSTS.spacestation}`,
};

/** Human-facing portal names for copy. SpaceStation is internal-only
 *  vocabulary and must never appear in customer-visible copy. */
export const PORTAL_LABELS: Record<PortalId, string> = {
  virtualspace: "VirtualSpace",
  spatialist: "Spatialist",
  spacestation: "SpaceStation",
};

/**
 * In-app route-group prefix per portal. The customer and partner
 * portals live under `/portal/*` and are exposed at the host root via
 * a `beforeFiles` host rewrite in `next.config.js`; SpaceStation keeps
 * the existing `/dashboard` namespace.
 */
export const PORTAL_PATH_PREFIXES: Record<PortalId, string> = {
  virtualspace: "/portal/vs",
  spatialist: "/portal/go",
  spacestation: "/dashboard",
};

/**
 * Email-first login router host (AD #158 ruling 2). Serves the
 * `/router/*` route group at its root via the `next.config.js` host
 * rewrite. It looks a visitor up and forwards to a portal LOGIN —
 * it never authenticates and holds no session. Distinct from the
 * spatialist.com LINK router below (stateless path-prefix 308s).
 */
export const LOGIN_ROUTER_HOST = "app.virtualspace.xyz";

/** Absolute origin for marketing / unknown-audience Sign in CTAs. */
export const LOGIN_ROUTER_ORIGIN = `https://${LOGIN_ROUTER_HOST}`;

export function isLoginRouterHost(host: string | null | undefined): boolean {
  return normaliseHost(host) === LOGIN_ROUTER_HOST;
}

/**
 * In-app route-group prefix for the login router. Exposed at the root
 * of {@link LOGIN_ROUTER_HOST} by a `next.config.js` host rewrite, so
 * only other hosts need the prefix.
 */
export const LOGIN_ROUTER_PATH_PREFIX = "/router";

/**
 * URL on the email-first login router (AD #158 ruling 2). Prefer this
 * over `operatorAppUrl("/login")` for public Sign-in CTAs — visitors
 * may belong to any of the three apps.
 *
 * Absolute (production router origin) by default. Pass the request
 * `host` to keep localhost and Preview links on the deployment being
 * tested; without it a Sign-in CTA rendered on a preview sends the
 * tester to production, where the flow cannot be verified. Mirrors
 * {@link buildPortalUrl}.
 */
export function loginRouterUrl(
  path: string = "/",
  host?: string | null,
): string {
  const clean = safeRelativePath(path);
  if (isLoginRouterHost(host)) return clean === "/" ? "/" : clean;
  if (isDeploymentLocalHost(host)) {
    return clean === "/"
      ? LOGIN_ROUTER_PATH_PREFIX
      : `${LOGIN_ROUTER_PATH_PREFIX}${clean}`;
  }
  return `${LOGIN_ROUTER_ORIGIN}${clean === "/" ? "" : clean}`;
}

/**
 * Legacy hosts with no dashboard/portal predicate of their own — 308
 * to {@link LOGIN_ROUTER_HOST} (see the matching `next.config.js`
 * redirect) instead of falling through to whatever real page happens
 * to exist at that path (marketing `/`, SpaceStation's password
 * `/login`). `vss.virtualspace.xyz` predates the router; kept alive
 * as a redirect rather than removed outright since it may still be
 * bookmarked/linked. Distinct from `PORTAL_HOST_ALIASES` above, which
 * redirects to a specific portal rather than the router.
 */
export const LOGIN_ROUTER_HOST_ALIASES = ["vss.virtualspace.xyz"];

/** Deliverability-domain audience prefixes (spatialist.com router). */
export const LINK_ROUTER_HOSTS = ["spatialist.com", "www.spatialist.com"] as const;
export const LINK_ROUTER_PREFIXES: Record<string, PortalId> = {
  vs: "virtualspace",
  go: "spatialist",
  hq: "spacestation",
};

/**
 * Normalise a caller-supplied path to a single-slash relative path.
 *
 * A leading `//` is a protocol-relative URL: returned bare from a
 * local-host branch it would send the visitor to an off-site origin.
 * No current caller can produce one — every path is built from a
 * DB-sourced id — but the guarantee should hold structurally rather
 * than by call-site discipline (pre-release security review, #1894).
 *
 * Collapsing slashes alone is not enough. WHATWG URL parsing treats
 * `\` as `/` for special schemes and strips tab/CR/LF before parsing,
 * so `/\evil.com` and `/<TAB>/evil.com` both reach an off-site origin.
 * Strip those first, then collapse.
 */
function safeRelativePath(path: string): string {
  const stripped = path.replace(/[\\\t\r\n]/g, "");
  const clean = stripped.startsWith("/") ? stripped : `/${stripped}`;
  return `/${clean.replace(/^\/+/, "")}`;
}

function normaliseHost(host: string | null | undefined): string {
  if (!host) return "";
  const colon = host.indexOf(":");
  const bare = colon === -1 ? host : host.slice(0, colon);
  return bare.toLowerCase();
}

/** Which portal a request host belongs to (null for every other host —
 *  marketing apexes, explore, localhost, previews). Canonical hosts
 *  ONLY — alias hosts resolve via `portalAliasForHost` and redirect. */
export function portalForHost(host: string | null | undefined): PortalId | null {
  const bare = normaliseHost(host);
  if (!bare) return null;
  if (bare === PORTAL_HOSTS.virtualspace) return "virtualspace";
  if (bare === PORTAL_HOSTS.spatialist) return "spatialist";
  if (bare === PORTAL_HOSTS.spacestation) return "spacestation";
  return null;
}

/**
 * Portal-relative path helper: on the portal's own host the rewrite
 * strips `/portal/vs|go`, so links should be host-relative; on any
 * other host (localhost, previews) the in-app prefix is required.
 * Client-safe (lives next to `portalForHost`).
 */
export function portalPath(
  portal: PortalId,
  path: string,
  host: string | null | undefined,
): string {
  const clean = safeRelativePath(path);
  if (portalForHost(host) === portal) return clean;
  const prefix = PORTAL_PATH_PREFIXES[portal];
  return `${prefix}${clean === "/" ? "" : clean}` || prefix;
}

/**
 * Whether a bare `/m/...` request should expand to `/portal/go/m/...`.
 * On `pro.spatialist.xyz` the host rewrite already maps `/m` into the
 * portal route group. Alias hosts 308 to the canonical host with the
 * path preserved, so they must keep the bare form too. Localhost,
 * previews, and every other host need the in-app prefix.
 */
export function shouldExpandSpatialistBareMissionPath(
  host: string | null | undefined,
): boolean {
  if (portalForHost(host) === "spatialist") return false;
  if (portalAliasForHost(host) === "spatialist") return false;
  return true;
}

/** Expand `/m` or `/m/<id>` to the prefixed portal path, or null. */
export function expandSpatialistBareMissionPath(
  pathname: string,
): string | null {
  if (pathname === "/m" || pathname.startsWith("/m/")) {
    return `/portal/go${pathname}`;
  }
  return null;
}

/** Which portal an ALIAS host redirects to (null for canonical and
 *  unknown hosts). */
export function portalAliasForHost(
  host: string | null | undefined,
): PortalId | null {
  const bare = normaliseHost(host);
  if (!bare) return null;
  return PORTAL_HOST_ALIASES[bare] ?? null;
}

export function isLinkRouterHost(host: string | null | undefined): boolean {
  const bare = normaliseHost(host);
  return bare.length > 0 && (LINK_ROUTER_HOSTS as readonly string[]).includes(bare);
}

/**
 * Resolve a `spatialist.com` path to its portal target.
 *
 *   /vs/orders/123?x=1 → https://my.virtualspace.xyz/orders/123?x=1
 *   /go/m/<id>         → https://pro.spatialist.xyz/m/<id>
 *   /hq/dashboard/...  → https://ops.virtualspace.xyz/dashboard/...
 *
 * Returns null for every other path — callers keep the legacy
 * behavior (301 to the same path on the marketing apex) so links
 * already in the wild continue to resolve. The router is stateless:
 * it never reads cookies, never fetches data, and never authorizes —
 * the destination portal enforces session + membership.
 */
export function resolveLinkRouterTarget(
  pathname: string,
  search?: string | null,
): string | null {
  if (pathname === "/get-started" || pathname === "/get-started/") {
    return "https://explore.virtualspace.xyz/get-started";
  }
  const match = pathname.match(/^\/(vs|go|hq)(\/.*)?$/);
  if (!match) return null;
  const portal = LINK_ROUTER_PREFIXES[match[1]];
  if (!portal) return null;
  const rest = match[2] && match[2] !== "/" ? match[2] : "/";
  const suffix = search ? (search.startsWith("?") ? search : `?${search}`) : "";
  return `${PORTAL_ORIGINS[portal]}${rest}${suffix}`;
}

/**
 * Hosts that serve a single deployment of the whole app rather than one
 * of the production audience hosts: localhost and Vercel previews. On
 * those, an absolute production portal URL walks the visitor off the
 * deployment under test, so links must stay same-origin.
 *
 * Deliberately a positive test for dev/preview rather than a list of
 * production hosts: an unrecognised host keeps the absolute production
 * behavior, so forgetting to enumerate a host can never strand a real
 * visitor on a path their host does not serve.
 */
export function isDeploymentLocalHost(host: string | null | undefined): boolean {
  const bare = normaliseHost(host);
  if (!bare) return false;
  return (
    bare === "localhost" ||
    bare === "127.0.0.1" ||
    bare === "0.0.0.0" ||
    bare.endsWith(".localhost") ||
    bare.endsWith(".local") ||
    bare.endsWith(".vercel.app")
  );
}

/**
 * Build a URL into a portal. `path` is portal-relative (e.g.
 * `/orders/<uuid>` for VirtualSpace, `/m/<missionId>` for Spatialist,
 * `/dashboard/<slug>/...` for SpaceStation).
 *
 * Absolute (production portal origin) by default. Pass the request
 * `host` to keep localhost and Preview links on the deployment being
 * tested — without it, a Sign-in CTA rendered on a preview sends the
 * tester to production, where the flow cannot be verified.
 */
export function buildPortalUrl(
  portal: PortalId,
  path: string,
  host?: string | null,
): string {
  const clean = safeRelativePath(path);
  if (isDeploymentLocalHost(host)) return portalPath(portal, clean, host);
  return `${PORTAL_ORIGINS[portal]}${clean}`;
}

/**
 * The `spatialist.com` deliverability form of a portal URL for email
 * and SMS bodies (`.com` reputation beats any `.xyz` in carrier and
 * spam filtering). 308s through the audience-prefix router above.
 */
export function buildDeliverabilityPortalUrl(portal: PortalId, path: string): string {
  const prefix =
    portal === "virtualspace" ? "vs" : portal === "spatialist" ? "go" : "hq";
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `https://spatialist.com/${prefix}${clean}`;
}
